/* ===========================================================================
   hc-wishlist-page.js - the Wishlist page and the cart's "Saved for later"
   ---------------------------------------------------------------------------
   Draws the shopper's lists from assets/hc-wishlist.js (window.HCWishlist).
   Every card is filled from /products/<handle>.js, so price, stock, photos
   and sizes are always current, whatever was saved. sections/hc-wishlist.liquid
   sets the mode:
   - page:  every list in tabs, each card with its sizes, Add to cart, Move to
            another list and Remove; Share, Add all to cart and list tools.
   - later: only "Saved for later", for the cart page: rows like the cart's
            own lines (photo, name, brand, size, price) with Move to cart and
            Remove, and nothing else - no tabs, counts or empty message.
   A shared link (?share=handle~variant,...&n=name) opens a read-only copy with
   Add to cart, a heart per item and "Save all to my wishlist".

   Customers only (owner, 2026-10-09): a signed-out guest sees a sign-in
   prompt here instead of a list, and "Save all" asks them to sign in first.
   With the guest page on (Apps > HC Wishlist > Guest page), that prompt is
   the guest view drawn by sections/hc-wishlist.liquid; this fills in what
   the guest tapped (assets/hc-wishlist.js holds it a day): one item as a
   card, more as a row of photos.
   Once the HC Wishlist app's account page is set up, customers keep their
   list in their account: this store page then only opens shared links and
   sends everyone else on to the account page.
   =========================================================================== */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const same = (a, b) => String(a) === String(b);
const keyOf = (item) => `${item.p}:${item.v || ''}`;
const plural = (n, one, other) => `${n} ${n === 1 ? one : other}`;
const MAX_SHARED = 60;

function cdn(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

/* ---------- product data, four requests at a time ---------- */

const products = new Map();
const queue = [];
let running = 0;

function pump() {
  while (running < 4 && queue.length) {
    const job = queue.shift();
    running += 1;
    job().finally(() => {
      running -= 1;
      pump();
    });
  }
}

/** Resolves {status: 'ok', product} | {status: 'gone'} (404) | {status: 'error'} (try again later). */
function loadProduct(root, handle) {
  if (products.has(handle)) return products.get(handle);
  const promise = new Promise((resolve) => {
    queue.push(() =>
      fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then(async (res) => {
          if (res.status === 404) return resolve({ status: 'gone' });
          if (!res.ok) throw new Error(String(res.status));
          resolve({ status: 'ok', product: await res.json() });
        })
        .catch(() => {
          products.delete(handle);
          resolve({ status: 'error' });
        })
    );
    pump();
  });
  products.set(handle, promise);
  return promise;
}

/* ---------- options and variants ---------- */

const optionNames = (product) => (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));

function optionValues(product, index) {
  const option = product.options?.[index];
  if (option && Array.isArray(option.values)) return option.values;
  return [...new Set(product.variants.map((v) => v.options[index]))];
}

const hasChoices = (product) => product.variants.length > 1;

function initialSelection(product, variantId) {
  const saved = variantId && product.variants.find((v) => same(v.id, variantId));
  if (saved) return [...saved.options];
  if (!hasChoices(product)) return [...product.variants[0].options];
  return optionNames(product).map((_, i) => {
    const values = optionValues(product, i);
    return values.length === 1 ? values[0] : null;
  });
}

function resolveVariant(product, sel) {
  if (sel.some((value) => value == null)) return null;
  return product.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
}

/** Is there an available variant with this value? Scoped like the product page: a
    colour (first option) is sold out only when every size is; a size is judged
    within the chosen colour. */
function valueAvailable(product, sel, index, value) {
  return product.variants.some(
    (v) => v.available && v.options[index] === value && v.options.every((o, i) => i >= index || sel[i] == null || o === sel[i])
  );
}

function imageFor(product, sel, variant) {
  if (variant?.featured_image?.src) return variant.featured_image.src;
  const match = product.variants.find((v) => v.featured_image && v.options.every((o, i) => sel[i] == null || o === sel[i]));
  return match?.featured_image?.src || product.featured_image || '';
}

/* ---------- cart ---------- */

async function postAdd(lines) {
  const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items: lines.map((line) => ({ id: Number(line.id), quantity: 1 })) }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok && !data.status, data };
}

/** Tells the theme (cart icon, cart page, the post-add popup) that the cart changed. */
async function announce(lines, added) {
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'wishlist',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: 1 })),
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError: false,
      items: cart.items,
      addedItem: added?.items?.[0],
      source: 'hc-wishlist',
      itemCount: lines.length,
      productId: lines[0]?.productId,
    },
  });
}

/* ---------- the page ---------- */

class HcWishlistPage extends HTMLElement {
  connectedCallback() {
    /* The cart only ever shows Saved for later (sections/hc-wishlist.liquid): never the
       whole wishlist, whose page mode sends shoppers on to the account's Wishlist page. */
    this.mode = this.dataset.mode === 'later' || /\/cart\/?$/.test(location.pathname) ? 'later' : 'page';
    this.format = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.uid = `hc-wlp-${this.dataset.sectionId || 'x'}`;
    try {
      this.text = JSON.parse(this.querySelector('[data-hc-wlp-text]')?.textContent || '{}');
    } catch {
      this.text = {};
    }
    this.app = this.querySelector('[data-hc-wlp-app]');
    this.cards = new Map();
    const start = () => this.start(window.HCWishlist);
    if (window.HCWishlist) start();
    else document.addEventListener('hc:wishlist:ready', start, { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener('hc:wishlist:change', this.onChange);
    window.removeEventListener('hashchange', this.onHash);
  }

  start(api) {
    if (this.api || !api || !this.app) return;
    this.api = api;
    this.root = api.root || '/';
    this.shared = this.mode === 'page' ? this.readShared() : null;
    this.active = this.mode === 'later' || location.hash === `#${api.LATER_ID}` ? api.LATER_ID : api.DEFAULT_ID;
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onSelect);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('keydown', this.onKey);
    document.addEventListener('hc:wishlist:change', this.onChange);
    window.addEventListener('hashchange', this.onHash);
    this.build();
  }

  money(cents) {
    return formatMoney(Number(cents) || 0, this.format, this.currency);
  }

  t(key, fallback) {
    return this.text[key] || fallback;
  }

  /* ----- shared links ----- */

  readShared() {
    const params = new URLSearchParams(location.search);
    const raw = params.get('share');
    if (raw == null) return null;
    const items = raw
      .split(',')
      .map((part) => {
        const [h = '', v = ''] = part.split('~');
        return { h: h.trim(), v: /^\d+$/.test(v) ? v : null };
      })
      .filter((item) => item.h && !/[/?#\s]/.test(item.h))
      .slice(0, MAX_SHARED);
    return { name: (params.get('n') || '').trim().slice(0, 40), items };
  }

  shareUrl(list) {
    /* Products the store no longer sells stay out of the link. */
    const live = list.items.filter((item) => this.cards.get(keyOf(item))?.data?.status !== 'gone');
    const parts = live.slice(0, MAX_SHARED).map((item) => `${encodeURIComponent(item.h)}~${item.v || ''}`);
    const base = new URL(this.api.storePageUrl || this.api.pageUrl, location.origin);
    base.hash = '';
    const query = `share=${parts.join(',')}&n=${encodeURIComponent(list.name)}`;
    return `${base.origin}${base.pathname}${base.search ? `${base.search}&` : '?'}${query}`;
  }

  /* ----- layout ----- */

  build() {
    if (this.shared) return this.buildShared();
    const guestBox = this.querySelector('[data-hc-wlg]');
    if (this.mode === 'page' && this.api.guestLocked && guestBox) return this.buildGuest(guestBox);
    if (this.mode === 'page' && this.api.storePageUrl && this.api.pageUrl !== this.api.storePageUrl) {
      /* The wishlist lives in the customer account now. Hearts tapped before signing in
         were just saved here: they go out before the page changes (at most a moment). */
      this.app.innerHTML = `<p class="hc-wlp__note">Your wishlist is in your account. <a href="${esc(this.api.pageUrl)}">Go to my wishlist</a></p>`;
      const go = () => location.replace(this.api.pageUrl);
      Promise.race([Promise.resolve(this.api.flushNow?.()), new Promise((resolve) => setTimeout(resolve, 2500))]).then(go, go);
      return;
    }
    if (this.mode === 'page' && this.api.guestLocked) {
      this.querySelector('.hc-wlp__note')?.setAttribute('hidden', '');
      this.app.innerHTML = `
        <div class="hc-wlp__empty">
          <p class="hc-wlp__empty-title">Sign in to see your wishlist</p>
          <p class="hc-wlp__empty-text">Your saved items are kept in your account, so they're there on every device.</p>
          <a class="button hc-wlp__empty-link" href="${esc(this.api.signInUrl())}">Sign in</a>
        </div>`;
      return;
    }
    if (this.mode === 'later') {
      this.app.innerHTML = `<ul class="hc-sfl__list" role="list" data-grid></ul>`;
      this.gridEl = this.app.querySelector('[data-grid]');
      this.update(true);
      return;
    }
    this.app.innerHTML = `
      <div class="hc-wlp__tabs" role="tablist" aria-label="Your lists" data-tabs hidden></div>
      <div class="hc-wlp__panel" id="${this.uid}-panel" data-panel>
        <div class="hc-wlp__tools" data-tools></div>
        <div class="hc-wlp__drawer" data-drawer hidden></div>
        <ul class="hc-wlp__grid" role="list" data-grid></ul>
        <div class="hc-wlp__empty" data-empty hidden></div>
      </div>`;
    this.tabsEl = this.app.querySelector('[data-tabs]');
    this.toolsEl = this.app.querySelector('[data-tools]');
    this.drawerEl = this.app.querySelector('[data-drawer]');
    this.gridEl = this.app.querySelector('[data-grid]');
    this.emptyEl = this.app.querySelector('[data-empty]');
    this.update(true);
  }

  /** Brings the page in line with the lists. Cards are only rebuilt when the items changed. */
  update(force = false) {
    const lists = this.api.lists();
    if (!lists.some((l) => l.id === this.active)) this.active = this.mode === 'later' ? this.api.LATER_ID : this.api.DEFAULT_ID;
    const list = lists.find((l) => l.id === this.active) || { id: this.active, name: 'Saved for later', items: [] };

    if (this.mode === 'later') {
      this.hidden = list.items.length === 0;
    } else {
      this.renderTabs(lists);
      this.renderTools(list);
    }

    const keys = list.items.map(keyOf).join(',');
    if (force || keys !== this.gridKeys) {
      this.gridKeys = keys;
      this.renderGrid(list);
    }
    this.renderEmpty(list);
  }

  onChange = (event) => {
    if (!this.api || this.shared) return;
    if (this.guestBox) {
      if (event?.detail?.reason === 'held') this.paintGuest();
      return;
    }
    this.update();
  };

  onHash = () => {
    if (this.mode === 'page' && !this.shared && location.hash === `#${this.api.LATER_ID}`) this.select(this.api.LATER_ID);
  };

  select(listId, focusTab = false) {
    if (listId === this.active) return;
    this.active = listId;
    this.closeDrawer();
    this.update(true);
    if (focusTab) this.tabsEl.querySelector(`[data-list="${CSS.escape(listId)}"]`)?.focus();
  }

  renderTabs(lists) {
    const show = lists.length > 1 || this.api.listsEnabled;
    this.tabsEl.hidden = !show;
    if (!show) return;
    const focused = this.tabsEl.contains(document.activeElement) ? document.activeElement.dataset.list || document.activeElement.dataset.act : null;
    this.tabsEl.innerHTML =
      lists
        .map((l) => {
          const on = l.id === this.active;
          return `<button type="button" class="hc-wlp__tab" role="tab" id="${this.uid}-tab-${esc(l.id)}" data-list="${esc(l.id)}" aria-selected="${on}" aria-controls="${this.uid}-panel" tabindex="${on ? 0 : -1}"><span class="hc-wlp__tab-name">${esc(l.name)}</span> <span class="hc-wlp__tab-count">${l.items.length}</span></button>`;
        })
        .join('') +
      (this.api.listsEnabled ? `<button type="button" class="hc-wlp__tab hc-wlp__tab--new" data-act="new-list">+ ${esc(this.t('new_list', 'New list'))}</button>` : '');
    const panel = this.app.querySelector('[data-panel]');
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', `${this.uid}-tab-${this.active}`);
    if (focused) this.tabsEl.querySelector(`[data-list="${CSS.escape(focused)}"], [data-act="${CSS.escape(focused)}"]`)?.focus();
  }

  renderTools(list) {
    const n = list.items.length;
    const custom = list.id !== this.api.DEFAULT_ID && list.id !== this.api.LATER_ID;
    const canShare = this.api.shareEnabled && list.id !== this.api.LATER_ID && n > 0;
    const addAll = this.dataset.addAll !== 'false' && n > 1;
    const focused = this.toolsEl.contains(document.activeElement) ? document.activeElement.dataset.act : null;
    this.toolsEl.innerHTML = `
      <p class="hc-wlp__count">${plural(n, 'item', 'items')}</p>
      <div class="hc-wlp__actions">
        ${canShare ? `<button type="button" class="hc-wlp__action" data-act="share" aria-expanded="${this.drawer === 'share'}" aria-controls="${this.uid}-drawer">${esc(this.t('share', 'Share'))}</button>` : ''}
        ${custom ? `<button type="button" class="hc-wlp__action" data-act="rename" aria-expanded="${this.drawer === 'rename'}" aria-controls="${this.uid}-drawer">Rename</button>` : ''}
        ${custom ? `<button type="button" class="hc-wlp__action" data-act="delete-list" aria-expanded="${this.drawer === 'delete-list'}" aria-controls="${this.uid}-drawer">Delete list</button>` : ''}
        ${!custom && n > 0 ? `<button type="button" class="hc-wlp__action" data-act="clear" aria-expanded="${this.drawer === 'clear'}" aria-controls="${this.uid}-drawer">Remove all</button>` : ''}
        ${addAll ? `<button type="button" class="button hc-wlp__add-all" data-act="add-all">${esc(this.t('add_all', 'Add all to cart'))}</button>` : ''}
      </div>`;
    this.drawerEl.id = `${this.uid}-drawer`;
    if (focused) this.toolsEl.querySelector(`[data-act="${CSS.escape(focused)}"]`)?.focus();
  }

  renderEmpty(list) {
    const empty = list.items.length === 0;
    this.gridEl.hidden = empty;
    if (this.mode === 'later' || !this.emptyEl) return;
    this.emptyEl.hidden = !empty;
    if (!empty) return;
    let title = this.t('empty_heading', 'Your wishlist is empty');
    let body = this.t('empty_text', 'Tap the heart on anything you like and it will wait for you here.');
    if (list.id === this.api.LATER_ID) {
      title = 'Nothing saved for later';
      body = 'Items you save from your cart show up here.';
    } else if (list.id !== this.api.DEFAULT_ID) {
      title = 'This list is empty';
      body = 'Tap the heart on a product, then Change list in the message that appears.';
    }
    const link = this.dataset.emptyLink || `${this.root}collections/all`;
    this.emptyEl.innerHTML = `
      <p class="hc-wlp__empty-title">${esc(title)}</p>
      <p class="hc-wlp__empty-text">${esc(body)}</p>
      <a class="button hc-wlp__empty-link" href="${esc(link)}">${esc(this.t('empty_label', 'Start shopping'))}</a>`;
  }

  /* ----- cards ----- */

  renderGrid(list) {
    const focus = this.gridEl.contains(document.activeElement)
      ? { key: document.activeElement.closest('[data-key]')?.dataset.key, act: document.activeElement.dataset.act, index: [...this.gridEl.children].indexOf(document.activeElement.closest('[data-key]')) }
      : null;
    const live = new Set();
    this.gridEl.innerHTML = list.items
      .map((item) => {
        const key = keyOf(item);
        live.add(key);
        let card = this.cards.get(key);
        if (!card) {
          card = { key, item: { ...item }, data: null, sel: null, variant: null };
          this.cards.set(key, card);
        }
        card.listId = list.id;
        card.item = { ...item };
        return this.cardHtml(card);
      })
      .join('');
    for (const key of this.cards.keys()) if (!live.has(key)) this.cards.delete(key);
    list.items.forEach((item) => this.hydrate(this.cards.get(keyOf(item))));
    if (focus) this.restoreFocus(focus);
  }

  restoreFocus({ key, act, index }) {
    const cards = [...this.gridEl.children];
    const card = cards.find((li) => li.dataset.key === key) || cards[Math.min(Math.max(index, 0), cards.length - 1)];
    const target = card?.querySelector(`[data-act="${act}"]`) || card?.querySelector('a, button, select');
    if (target) target.focus();
    else this.querySelector('.hc-wlp__title')?.focus();
  }

  hydrate(card) {
    if (!card || card.data) return;
    loadProduct(this.root, card.item.h).then((data) => {
      if (data.status === 'ok' && !same(data.product.id, card.item.p) && card.item.p) data = { status: 'gone' };
      card.data = data;
      if (data.status === 'ok') {
        card.sel = initialSelection(data.product, card.item.v);
        card.variant = resolveVariant(data.product, card.sel);
      }
      this.paintCard(card);
    });
  }

  paintCard(card) {
    const li = this.gridEl?.querySelector(`[data-key="${CSS.escape(card.key)}"]`);
    if (!li) return;
    const had = li.contains(document.activeElement) ? document.activeElement.dataset.act || document.activeElement.dataset.opt : null;
    li.outerHTML = this.cardHtml(card);
    if (had != null) {
      const fresh = this.gridEl.querySelector(`[data-key="${CSS.escape(card.key)}"]`);
      fresh?.querySelector(`[data-act="${had}"], [data-opt="${had}"]`)?.focus();
    }
  }

  cardHtml(card) {
    if (this.mode === 'later') return this.rowHtml(card);
    const { item, data } = card;
    const product = data?.status === 'ok' ? data.product : null;
    const isLater = card.listId === this.api.LATER_ID;
    const shared = card.listId === '__shared';
    const title = product?.title || item.n || item.h.replace(/-/g, ' ');
    const variant = card.variant;
    const url = product ? `${product.url}${variant && hasChoices(product) ? `?variant=${variant.id}` : ''}` : `${this.root}products/${encodeURIComponent(item.h)}`;
    const img = product ? imageFor(product, card.sel, variant) : '';

    let media = `<span class="hc-wlp__media-blank"></span>`;
    if (img) {
      media = `<img src="${esc(cdn(img, 480))}" srcset="${esc(cdn(img, 360))} 360w, ${esc(cdn(img, 480))} 480w, ${esc(cdn(img, 720))} 720w" sizes="(min-width: 990px) 280px, 45vw" alt="" loading="lazy" decoding="async" width="480" height="480">`;
    }

    let corner = '';
    if (shared && product) {
      corner = `<button type="button" class="hc-wl-heart hc-wlp__heart" data-hc-wl-heart data-product-id="${product.id}" data-handle="${esc(product.handle)}" data-title="${esc(product.title)}" data-vendor="${esc(product.vendor)}" data-price="${variant ? variant.price : product.price}" aria-pressed="${this.api.has(product.id)}" aria-label="Save ${esc(product.title)} to your wishlist"><span class="hc-wl-heart__icon" aria-hidden="true"></span></button>`;
    } else if (!shared) {
      corner = `<button type="button" class="hc-wlp__remove" data-act="remove" aria-label="Remove ${esc(title)}"></button>`;
    }

    let price = '';
    if (product) {
      const now = variant ? variant.price : product.price;
      const was = variant ? variant.compare_at_price : product.compare_at_price;
      const from = !variant && product.price_varies ? 'From ' : '';
      const sale = was && was > now;
      price = `<p class="hc-wlp__price${sale ? ' hc-wlp__price--sale' : ''}"><span class="visually-hidden">${sale ? 'Sale price' : 'Price'}</span>${from}${esc(this.money(now))}${sale ? ` <s><span class="visually-hidden">Regular price</span>${esc(this.money(was))}</s>` : ''}</p>`;
    }

    let opts = '';
    if (product && hasChoices(product)) {
      opts = optionNames(product)
        .map((name, i) => {
          const values = optionValues(product, i);
          const chosen = card.sel[i];
          const options = values
            .map((value) => {
              const soldOut = !valueAvailable(product, card.sel, i, value);
              return `<option value="${esc(value)}"${value === chosen ? ' selected' : ''}>${esc(value)}${soldOut ? ' - sold out' : ''}</option>`;
            })
            .join('');
          const placeholder = chosen == null ? `<option value="" selected disabled>Choose ${esc(name.toLowerCase())}</option>` : '';
          return `<label class="hc-wlp__opt"><span class="visually-hidden">${esc(name)}</span><select class="hc-wlp__select" data-opt="${i}">${placeholder}${options}</select></label>`;
        })
        .join('');
      opts = `<div class="hc-wlp__opts">${opts}</div>`;
    }

    let status = '';
    let canAdd = false;
    if (!data) status = '';
    else if (data.status === 'gone') status = 'No longer available';
    else if (data.status === 'error') status = 'Could not load this item. Refresh to try again.';
    else if (!product.available) status = 'Sold out';
    else if (variant && !variant.available) status = `Sold out in this ${(optionNames(product).at(-1) || 'option').toLowerCase()}`;
    else canAdd = true;
    if (card.notice) status = card.notice;

    const addLabel = isLater ? 'Move to cart' : this.t('add', 'Add to cart');
    const soldOut = product && (!product.available || (variant && !variant.available));
    const button = product
      ? `<button type="button" class="hc-wlp__add" data-act="add"${canAdd ? '' : ' disabled'}${card.busy ? ' data-state="busy"' : ''}>${soldOut ? 'Sold out' : card.busy ? 'Adding...' : esc(card.added ? 'Added' : addLabel)}</button>`
      : '';

    let links = '';
    if (!shared) {
      const targets = this.api.lists().filter((l) => l.id !== card.listId && l.id !== this.api.LATER_ID);
      if (isLater && targets.length) {
        links = `<button type="button" class="hc-wlp__link" data-act="to-wishlist">Move to ${esc(targets[0].name.toLowerCase() === 'wishlist' ? 'wishlist' : targets[0].name)}</button>`;
      } else if (!isLater && targets.length) {
        links = `<label class="hc-wlp__move"><span class="visually-hidden">Move ${esc(title)} to another list</span><select class="hc-wlp__select hc-wlp__select--move" data-act="move"><option value="" selected disabled>Move to...</option>${targets.map((l) => `<option value="${esc(l.id)}">${esc(l.name)}</option>`).join('')}</select></label>`;
      }
    }

    return `<li class="hc-wlp__card${data ? '' : ' hc-wlp__card--loading'}" data-key="${esc(card.key)}">
      <div class="hc-wlp__media"><a href="${esc(url)}" tabindex="-1" aria-hidden="true">${media}</a>${corner}</div>
      ${product?.vendor ? `<p class="hc-wlp__vendor">${esc(product.vendor)}</p>` : ''}
      <h3 class="hc-wlp__name"><a href="${esc(url)}">${esc(title)}</a></h3>
      ${price}
      ${opts}
      <p class="hc-wlp__status" role="status">${esc(status)}</p>
      ${button}
      ${links ? `<div class="hc-wlp__links">${links}</div>` : ''}
    </li>`;
  }

  /** A Saved for later row, laid out like the cart's own line (snippets/cart-products):
      photo, name, brand, size and price, then Move to cart and Remove. The price sits on
      the right on computers and under the name on phones, as in the cart. */
  rowHtml(card) {
    const { item, data } = card;
    const product = data?.status === 'ok' ? data.product : null;
    const variant = card.variant;
    const title = product?.title || item.n || item.h.replace(/-/g, ' ');
    const url = product ? `${product.url}${variant && hasChoices(product) ? `?variant=${variant.id}` : ''}` : `${this.root}products/${encodeURIComponent(item.h)}`;
    const img = product ? imageFor(product, card.sel, variant) : '';
    const media = img
      ? `<img src="${esc(cdn(img, 320))}" srcset="${esc(cdn(img, 160))} 160w, ${esc(cdn(img, 320))} 320w" sizes="140px" alt="" loading="lazy" decoding="async" width="320" height="320">`
      : '';
    const sizes = product && variant && hasChoices(product) ? variant.options.join(', ') : '';

    let price = '';
    if (product) {
      const now = variant ? variant.price : product.price;
      const was = variant ? variant.compare_at_price : product.compare_at_price;
      const from = !variant && product.price_varies ? 'From ' : '';
      price =
        was && was > now
          ? `<span class="visually-hidden">Sale price</span><span class="hc-sfl__now">${from}${esc(this.money(now))}</span> <span class="visually-hidden">Regular price</span><s class="compare-at-price">${esc(this.money(was))}</s>`
          : `<span class="visually-hidden">Price</span>${from}${esc(this.money(now))}`;
    }

    let status = card.notice || '';
    let move = '';
    if (data?.status === 'gone') status ||= 'No longer available';
    else if (data?.status === 'error') status ||= 'Could not load this item. Refresh to try again.';
    else if (product && (!product.available || (variant && !variant.available))) status ||= 'Sold out';
    else if (product && !variant) move = `<a class="hc-sfl__action" href="${esc(url)}">Choose a size</a>`;
    else if (product) {
      const look = this.dataset.moveStyle === 'button' ? 'button button-secondary hc-sfl__button' : 'hc-sfl__action';
      move = `<button type="button" class="${look}" data-act="add"${card.busy ? ' disabled' : ''}>${esc(card.busy ? 'Moving...' : this.t('move', 'Move to cart'))}</button>`;
    }
    const removeLabel = this.t('remove', 'Remove');

    return `<li class="hc-sfl__row${data ? '' : ' hc-sfl__row--loading'}" data-key="${esc(card.key)}">
      <a class="hc-sfl__media" href="${esc(url)}" tabindex="-1" aria-hidden="true">${media}</a>
      <div class="hc-sfl__details cart-primary-typography">
        <a class="hc-sfl__name" href="${esc(url)}">${esc(title)}</a>
        ${this.dataset.brand !== 'false' && product?.vendor ? `<p>${esc(product.vendor)}</p>` : ''}
        ${sizes ? `<p>${esc(sizes)}</p>` : ''}
        ${price ? `<p class="hc-sfl__unit">${price}</p>` : ''}
        <p class="hc-sfl__status" role="status">${esc(status)}</p>
        <div class="hc-sfl__actions">
          ${move}
          <button type="button" class="hc-sfl__action" data-act="remove" aria-label="${esc(`${removeLabel} ${title}`)}">${esc(removeLabel)}</button>
        </div>
      </div>
      <p class="hc-sfl__price cart-secondary-typography">${price}</p>
    </li>`;
  }

  cardFrom(el) {
    const key = el.closest('[data-key]')?.dataset.key;
    return key ? this.cards.get(key) : null;
  }

  /* ----- events ----- */

  onSelect = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const card = this.cardFrom(select);
    if (!card) return;

    if (select.dataset.act === 'move') {
      const to = select.value;
      const target = this.api.lists().find((l) => l.id === to);
      if (!target) return;
      const from = card.listId;
      this.api.moveEntry(from, to, card.item.p, card.item.v);
      this.api.toast({
        text: `Moved to ${target.name}`,
        action: { label: this.api.words?.undo || 'Undo', run: () => this.api.moveEntry(to, from, card.item.p, card.item.v) },
      });
      return;
    }

    const product = card.data?.product;
    if (!product) return;
    const index = Number(select.dataset.opt);
    card.sel[index] = select.value;
    card.variant = resolveVariant(product, card.sel);
    card.notice = '';
    card.added = false;
    /* Keep the choice with the saved item, so it is there next time. Re-key the
       card first: the list change that follows then finds nothing to rebuild. */
    if (card.variant && card.listId !== '__shared' && !same(card.variant.id, card.item.v)) {
      const oldV = card.item.v;
      const newKey = `${card.item.p}:${card.variant.id}`;
      if (!this.cards.has(newKey)) {
        const li = this.gridEl.querySelector(`[data-key="${CSS.escape(card.key)}"]`);
        if (li) li.dataset.key = newKey;
        this.cards.delete(card.key);
        card.key = newKey;
        card.item.v = String(card.variant.id);
        this.cards.set(newKey, card);
        this.gridKeys = this.api
          .lists()
          .find((l) => l.id === card.listId)
          ?.items.map((i) => (same(i.p, card.item.p) && (i.v || null) === (oldV || null) ? newKey : keyOf(i)))
          .join(',');
      }
      /* With that size already in the list, the two entries become one and the grid redraws. */
      this.api.setVariant(card.listId, card.item.p, oldV, card.variant.id);
    }
    this.paintCard(card);
  };

  onClick = (event) => {
    const shareLink = event.target instanceof Element ? event.target.closest('[data-share-method]') : null;
    if (shareLink && this.contains(shareLink)) return this.track(shareLink.dataset.shareMethod);
    const target = event.target instanceof Element ? event.target.closest('[data-act], [data-list]') : null;
    if (!target || !this.contains(target)) return;
    if (target.dataset.list && target.getAttribute('role') === 'tab') return this.select(target.dataset.list);
    const act = target.dataset.act;
    const card = this.cardFrom(target);
    switch (act) {
      case 'add':
        return this.addOne(card);
      case 'remove':
        return this.removeOne(card);
      case 'to-wishlist':
        this.api.moveEntry(card.listId, this.api.DEFAULT_ID, card.item.p, card.item.v);
        return this.api.toast({ text: 'Moved to your wishlist' });
      case 'add-all':
        return this.addAll(target);
      case 'share':
      case 'rename':
      case 'delete-list':
      case 'clear':
        return this.toggleDrawer(act);
      case 'drawer-close':
        return this.closeDrawer(true);
      case 'copy':
        return this.copyLink(target);
      case 'native-share':
        return this.nativeShare();
      case 'confirm-delete':
        return this.deleteList();
      case 'confirm-clear':
        return this.clearList();
      case 'new-list':
        return this.toggleDrawer('new-list');
      case 'save-shared':
        return this.saveShared(target);
      default:
    }
  };

  onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;
    event.preventDefault();
    const name = form.elements.namedItem('name')?.value.trim();
    if (!name) return;
    if (form.dataset.form === 'rename') {
      this.api.renameList(this.active, name);
      this.closeDrawer(true);
    } else if (form.dataset.form === 'new-list') {
      const id = this.api.createList(name);
      this.closeDrawer();
      this.select(id, true);
    }
  };

  /* Tabs: arrow keys move between lists (the tablist pattern). */
  onKey = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...this.tabsEl.querySelectorAll('[role="tab"]')];
    let i = tabs.indexOf(tab);
    if (event.key === 'ArrowLeft') i = (i - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') i = (i + 1) % tabs.length;
    if (event.key === 'Home') i = 0;
    if (event.key === 'End') i = tabs.length - 1;
    event.preventDefault();
    this.select(tabs[i].dataset.list, true);
  };

  /* ----- actions ----- */

  needsChoice(card) {
    const product = card.data?.product;
    if (!product || card.variant) return false;
    const missing = card.sel.findIndex((value) => value == null);
    if (missing < 0) return false;
    card.notice = `Choose a ${optionNames(product)[missing].toLowerCase()} first`;
    this.paintCard(card);
    this.gridEl.querySelector(`[data-key="${CSS.escape(card.key)}"] [data-opt="${missing}"]`)?.focus();
    return true;
  }

  async addOne(card) {
    if (!card || card.busy || this.needsChoice(card)) return;
    const variant = card.variant;
    if (!variant?.available) return;
    card.busy = true;
    card.notice = '';
    this.paintCard(card);
    const line = { id: variant.id, productId: card.data.product.id };
    try {
      const { ok, data } = await postAdd([line]);
      if (!ok) throw new Error(data.description || 'Could not add to cart');
      await announce([line], data);
      card.busy = false;
      if (card.listId === this.api.LATER_ID) {
        this.api.removeEntry(this.api.LATER_ID, card.item.p, card.item.v);
        this.api.toast({ text: this.api.words?.moved || 'Moved to your cart' });
        return;
      }
      card.added = true;
      this.paintCard(card);
      setTimeout(() => {
        card.added = false;
        this.paintCard(card);
      }, 2200);
    } catch (error) {
      card.busy = false;
      card.notice = error?.message || 'Could not add to cart';
      this.paintCard(card);
    }
  }

  removeOne(card) {
    if (!card) return;
    const removed = this.api.removeEntry(card.listId, card.item.p, card.item.v);
    const list = this.api.lists().find((l) => l.id === card.listId);
    this.api.toast({
      text: `Removed from ${list && list.id !== this.api.DEFAULT_ID ? list.name : 'your wishlist'}`,
      action: { label: this.api.words?.undo || 'Undo', run: () => this.api.restore(removed) },
    });
  }

  async addAll(button) {
    const list = this.api.lists().find((l) => l.id === this.active);
    if (!list || button.dataset.state === 'busy') return;
    const cards = list.items.map((item) => this.cards.get(keyOf(item))).filter(Boolean);
    const lines = [];
    let skipped = 0;
    for (const card of cards) {
      if (card.variant?.available) lines.push({ id: card.variant.id, productId: card.data.product.id, card });
      else skipped += 1;
    }
    if (!lines.length) {
      this.api.toast({ text: skipped ? 'Choose sizes first - nothing here can be added yet.' : 'Nothing to add.' });
      return;
    }
    button.dataset.state = 'busy';
    button.disabled = true;
    button.textContent = 'Adding...';
    let added = [];
    let failed = 0;
    let last = null;
    const batch = await postAdd(lines).catch(() => ({ ok: false, data: {} }));
    if (batch.ok) {
      added = lines;
      last = batch.data;
    } else {
      /* One line can sink the whole request (a size sold out a moment ago): add them one by one. */
      for (const line of lines) {
        const one = await postAdd([line]).catch(() => ({ ok: false, data: {} }));
        if (one.ok) {
          added.push(line);
          last = one.data;
        } else failed += 1;
      }
    }
    if (added.length) await announce(added, last).catch(() => {});
    if (list.id === this.api.LATER_ID) added.forEach((line) => this.api.removeEntry(this.api.LATER_ID, line.card.item.p, line.card.item.v));
    button.dataset.state = '';
    button.disabled = false;
    button.textContent = this.t('add_all', 'Add all to cart');
    const notes = [];
    if (skipped) notes.push(`${plural(skipped, 'item needs', 'items need')} a size or ${skipped === 1 ? 'is' : 'are'} sold out`);
    if (failed) notes.push(`${plural(failed, 'item', 'items')} could not be added`);
    this.api.toast({ text: `Added ${plural(added.length, 'item', 'items')} to your cart${notes.length ? `. ${notes.join('; ')}.` : ''}` });
  }

  /* ----- the drawer under the tools: share, rename, delete, clear, new list ----- */

  toggleDrawer(kind) {
    if (this.drawer === kind) return this.closeDrawer(true);
    const list = this.api.lists().find((l) => l.id === this.active);
    if (!list) return;
    this.drawer = kind;
    let html = '';
    if (kind === 'share') {
      const url = this.shareUrl(list);
      const text = `${list.name} - High Country Outfitters`;
      const enc = encodeURIComponent;
      html = `
        <label class="hc-wlp__label" for="${this.uid}-link">Link to this list</label>
        <div class="hc-wlp__row">
          <input class="hc-wlp__input" id="${this.uid}-link" type="text" readonly value="${esc(url)}">
          <button type="button" class="button hc-wlp__copy" data-act="copy">Copy link</button>
        </div>
        <ul class="hc-wlp__share-links" role="list">
          <li><a href="mailto:?subject=${enc(text)}&body=${enc(url)}" data-share-method="email">Email</a></li>
          <li><a href="sms:?&body=${enc(`${text} ${url}`)}" data-share-method="text">Text</a></li>
          <li><a href="https://www.facebook.com/sharer/sharer.php?u=${enc(url)}" target="_blank" rel="noopener" data-share-method="facebook">Facebook</a></li>
          <li><a href="https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}" target="_blank" rel="noopener" data-share-method="x">X</a></li>
          <li><a href="https://pinterest.com/pin/create/button/?url=${enc(url)}&description=${enc(text)}" target="_blank" rel="noopener" data-share-method="pinterest">Pinterest</a></li>
          <li><a href="https://wa.me/?text=${enc(`${text} ${url}`)}" target="_blank" rel="noopener" data-share-method="whatsapp">WhatsApp</a></li>
          ${navigator.share ? '<li><button type="button" class="hc-wlp__link" data-act="native-share">More...</button></li>' : ''}
        </ul>
        <p class="hc-wlp__hint">The link shows this list as it is now. Anyone with it can see the items, not your name or account.</p>`;
    } else if (kind === 'rename' || kind === 'new-list') {
      const rename = kind === 'rename';
      html = `
        <form class="hc-wlp__row" data-form="${kind}">
          <label class="visually-hidden" for="${this.uid}-name">${rename ? 'List name' : 'New list name'}</label>
          <input class="hc-wlp__input" id="${this.uid}-name" name="name" type="text" maxlength="40" required value="${rename ? esc(list.name) : ''}" placeholder="${rename ? '' : 'Name your list'}">
          <button type="submit" class="button hc-wlp__copy">${rename ? 'Save' : 'Create'}</button>
          <button type="button" class="hc-wlp__link" data-act="drawer-close">Cancel</button>
        </form>`;
    } else if (kind === 'delete-list') {
      html = `
        <p class="hc-wlp__confirm-text">Delete "${esc(list.name)}"${list.items.length ? ` and its ${plural(list.items.length, 'item', 'items')}` : ''}?</p>
        <div class="hc-wlp__row">
          <button type="button" class="button hc-wlp__copy" data-act="confirm-delete">Delete list</button>
          <button type="button" class="hc-wlp__link" data-act="drawer-close">Cancel</button>
        </div>`;
    } else if (kind === 'clear') {
      html = `
        <p class="hc-wlp__confirm-text">Remove all ${plural(list.items.length, 'item', 'items')} from ${list.id === this.api.DEFAULT_ID ? 'your wishlist' : `"${esc(list.name)}"`}?</p>
        <div class="hc-wlp__row">
          <button type="button" class="button hc-wlp__copy" data-act="confirm-clear">Remove all</button>
          <button type="button" class="hc-wlp__link" data-act="drawer-close">Cancel</button>
        </div>`;
    }
    this.drawerEl.innerHTML = html;
    this.drawerEl.hidden = false;
    this.toolsEl.querySelectorAll('[aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', String(b.dataset.act === kind)));
    (this.drawerEl.querySelector('input:not([readonly])') || this.drawerEl.querySelector('input, button'))?.focus();
  }

  closeDrawer(returnFocus = false) {
    const was = this.drawer;
    this.drawer = null;
    if (!this.drawerEl) return;
    this.drawerEl.hidden = true;
    this.drawerEl.innerHTML = '';
    this.toolsEl?.querySelectorAll('[aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (returnFocus && was) (this.toolsEl.querySelector(`[data-act="${was}"]`) || this.tabsEl.querySelector('[data-act="new-list"]'))?.focus();
  }

  async copyLink(button) {
    const input = this.drawerEl.querySelector('input');
    if (!input) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(input.value);
      copied = true;
    } catch {
      input.select();
      copied = document.execCommand?.('copy') ?? false;
    }
    button.textContent = copied ? 'Copied' : 'Select and copy';
    this.track('copy_link');
    setTimeout(() => {
      if (button.isConnected) button.textContent = 'Copy link';
    }, 2000);
  }

  async nativeShare() {
    const list = this.api.lists().find((l) => l.id === this.active);
    if (!list || !navigator.share) return;
    try {
      await navigator.share({ title: list.name, text: `${list.name} - High Country Outfitters`, url: this.shareUrl(list) });
      this.track('native');
    } catch {
      /* the shopper closed the share sheet */
    }
  }

  track(method) {
    try {
      window.gtag?.('event', 'share', { method, content_type: 'wishlist', item_id: this.active });
    } catch {
      /* ignore */
    }
  }

  deleteList() {
    const list = this.api.lists().find((l) => l.id === this.active);
    if (!list) return;
    this.closeDrawer();
    this.api.deleteList(list.id);
    this.active = this.api.DEFAULT_ID;
    this.update(true);
    this.api.toast({ text: `Deleted "${list.name}"` });
    this.tabsEl.querySelector('[aria-selected="true"]')?.focus();
  }

  clearList() {
    const list = this.api.lists().find((l) => l.id === this.active);
    if (!list) return;
    this.closeDrawer();
    const removed = this.api.clearList(list.id);
    this.api.toast({ text: 'Removed all items', action: { label: this.api.words?.undo || 'Undo', run: () => this.api.restore(removed) } });
    this.querySelector('.hc-wlp__title')?.focus();
  }

  /* ----- a shared list ----- */

  /* ----- the guest page ----- */

  buildGuest(box) {
    this.guestBox = box;
    /* Sign in comes back to this page (keeping its address), which saves what was held
       and then opens the account's Wishlist page. */
    box.querySelector('[data-hc-wlg-signin]')?.setAttribute('href', this.api.signInUrl());
    this.phone = window.matchMedia('(max-width: 749px)');
    this.phone.addEventListener?.('change', () => this.paintGuest());
    this.paintGuest();
  }

  /** The title for what's held, and its card or photos (the inline script in the section
      drew the same before the first paint, so nothing moves). */
  paintGuest() {
    const box = this.guestBox;
    const held = box.dataset.items === 'true' ? this.api.held() : [];
    const title = box.querySelector('[data-hc-wlg-title]');
    const items = box.querySelector('[data-hc-wlg-items]');
    const words =
      held.length === 0 ? title.dataset.title : held.length === 1 ? title.dataset.one : title.dataset.many.replace(/\{count\}/g, String(held.length));
    if (title.textContent !== words) title.textContent = words;
    if (!held.length) {
      items.hidden = true;
      items.replaceChildren();
      return;
    }
    items.hidden = false;
    if (held.length === 1) {
      const [item] = held;
      items.innerHTML = `
        <div class="hc-wlg__item">
          <span class="hc-wlg__photo"></span>
          <span class="hc-wlg__info">
            <span class="hc-wlg__name">${esc(item.title)}</span>
          </span>
          <span class="hc-wlg__heart" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" focusable="false"><path d="M12 20s-7.5-4.45-7.5-10.1A4.2 4.2 0 0 1 12 7.35a4.2 4.2 0 0 1 7.5 2.55C19.5 15.55 12 20 12 20Z"/></svg></span>
        </div>`;
      this.fillGuestItem(items.querySelector('.hc-wlg__item'), item);
      return;
    }
    const room = this.phone?.matches ? 4 : 5;
    const shown = held.length > room ? held.slice(0, room - 1) : held;
    const more = held.length - shown.length;
    items.innerHTML = `<ul class="hc-wlg__tiles" role="list">${shown.map(() => '<li class="hc-wlg__tile"></li>').join('')}${
      more ? `<li class="hc-wlg__tile hc-wlg__tile--more" aria-label="and ${more} more">+${more}</li>` : ''
    }</ul>`;
    const tiles = items.querySelectorAll('.hc-wlg__tile:not(.hc-wlg__tile--more)');
    shown.forEach((item, i) => this.fillGuestTile(tiles[i], item));
  }

  /** The held item's current photo, brand, name and price (a product the store no longer
      sells is let go of, and the view redrawn). */
  async fillGuestItem(card, item) {
    const data = await loadProduct(this.root, item.h);
    if (!card.isConnected) return;
    if (data.status === 'gone') return this.api.unhold(item.p);
    if (data.status !== 'ok') return;
    const product = data.product;
    const variant = item.v ? product.variants.find((v) => same(v.id, item.v)) : null;
    const src = variant ? imageFor(product, [...variant.options], variant) : product.featured_image;
    const varies = !variant && product.price_varies;
    const price = variant ? variant.price : product.price_min ?? product.price;
    card.querySelector('.hc-wlg__photo').innerHTML = src ? `<img src="${esc(cdn(src, 200))}" alt="" width="72" height="72">` : '';
    card.querySelector('.hc-wlg__info').innerHTML = `
      ${product.vendor ? `<span class="hc-wlg__brand">${esc(product.vendor)}</span>` : ''}
      <span class="hc-wlg__name">${esc(product.title)}${variant && hasChoices(product) ? ` <span class="hc-wlg__brand">${esc(variant.title)}</span>` : ''}</span>
      <span class="hc-wlg__price">${varies ? 'From ' : ''}${esc(this.money(price))}</span>`;
  }

  async fillGuestTile(tile, item) {
    const data = await loadProduct(this.root, item.h);
    if (!tile?.isConnected) return;
    if (data.status === 'gone') return this.api.unhold(item.p);
    if (data.status !== 'ok') return;
    const product = data.product;
    const variant = item.v ? product.variants.find((v) => same(v.id, item.v)) : null;
    const src = variant ? imageFor(product, [...variant.options], variant) : product.featured_image;
    if (src) tile.innerHTML = `<img src="${esc(cdn(src, 240))}" alt="${esc(product.title)}" width="96" height="96">`;
    else tile.setAttribute('aria-label', product.title);
  }

  buildShared() {
    const { name, items } = this.shared;
    const title = this.querySelector('.hc-wlp__title');
    if (title) title.textContent = name && name !== 'Wishlist' ? name : 'A shared wishlist';
    this.querySelector('.hc-wlp__note')?.setAttribute('hidden', '');
    this.app.innerHTML = `
      <div class="hc-wlp__tools">
        <p class="hc-wlp__count">${plural(items.length, 'item', 'items')}, shared with you</p>
        <div class="hc-wlp__actions">
          <a class="hc-wlp__action" href="${esc(this.api.guestLocked ? this.api.signInUrl() : this.api.pageUrl)}">Your wishlist</a>
          ${items.length ? `<button type="button" class="button hc-wlp__add-all" data-act="save-shared">Save all to my wishlist</button>` : ''}
        </div>
      </div>
      <ul class="hc-wlp__grid" role="list" data-grid></ul>
      <div class="hc-wlp__empty" data-empty ${items.length ? 'hidden' : ''}>
        <p class="hc-wlp__empty-title">This shared list is empty</p>
        <a class="button hc-wlp__empty-link" href="${esc(this.dataset.emptyLink || `${this.root}collections/all`)}">${esc(this.t('empty_label', 'Start shopping'))}</a>
      </div>`;
    this.gridEl = this.app.querySelector('[data-grid]');
    this.gridEl.innerHTML = items
      .map((item, i) => {
        const card = { key: `s${i}:${item.v || ''}`, listId: '__shared', item: { p: null, h: item.h, v: item.v }, data: null, sel: null, variant: null };
        this.cards.set(card.key, card);
        return this.cardHtml(card);
      })
      .join('');
    const loads = [...this.cards.values()].map((card) =>
      loadProduct(this.root, card.item.h).then((data) => {
        card.data = data;
        if (data.status === 'ok') {
          card.item.p = String(data.product.id);
          card.sel = initialSelection(data.product, card.item.v);
          card.variant = resolveVariant(data.product, card.sel);
        }
        this.paintCard(card);
      })
    );
    /* Back from signing in to save this list: finish the job. */
    if (this.api.takePending('shared')) {
      Promise.all(loads).then(() => {
        const button = this.app.querySelector('[data-act="save-shared"]');
        if (button) this.saveShared(button);
      });
    }
  }

  async saveShared(button) {
    if (this.api.guestLocked) {
      this.api.askToSignIn({ kind: 'shared' }, 'Sign in to save this list');
      return;
    }
    const ready = [...this.cards.values()].filter((card) => card.data?.status === 'ok');
    if (!ready.length) return;
    let listId = this.api.DEFAULT_ID;
    if (this.api.listsEnabled && this.shared.name) {
      const existing = this.api.lists().find((l) => l.name === this.shared.name);
      listId = existing ? existing.id : this.api.createList(this.shared.name);
    }
    let count = 0;
    ready.forEach((card) => {
      const p = card.data.product;
      if (this.api.add({ p: p.id, h: p.handle, v: card.variant?.id || card.item.v, title: p.title, price: card.variant?.price || p.price }, listId)) count += 1;
    });
    button.textContent = 'Saved';
    button.disabled = true;
    const list = this.api.lists().find((l) => l.id === listId);
    this.api.toast({
      text: count ? `Saved ${plural(count, 'item', 'items')} to ${list && listId !== this.api.DEFAULT_ID ? list.name : 'your wishlist'}` : 'Already in your lists',
      link: { label: this.api.words?.view || 'View', href: this.api.pageUrl },
    });
  }
}

if (!customElements.get('hc-wishlist-page')) customElements.define('hc-wishlist-page', HcWishlistPage);
